import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, useToastStore } from '@/shared/ui';

import {
  dormantList,
  draugrList,
  firedList,
  hangingList,
  launchedList,
  noticeList,
  iterationList,
} from '../model/dashboard';
import { waitingSince } from '../model/ordering';
import type { CommitmentSummary, Dashboard, DashboardNotice, LaunchedCommitment } from '../model/types';
import { useAcknowledgeNotice, useDashboard } from '../queries/useCommitments';
import { CloseButtons } from './CloseButtons';
import { TILE_ICONS } from './tileIcons';
import styles from './WorkList.module.css';
import { AgeCell, ElementLink, WorkListLayout } from './WorkListLayout';

/**
 * Sześć list z menu — **osobne ekrany**, wspólny szkielet.
 *
 * ⭐ Rola tych ekranów wynika z decyzji 63: *„listy z pulpitu i z menu odpowiadają na
 * «co jest na mnie»"*, w odróżnieniu od Constellation, które odpowiada na „co się dzieje
 * wokół elementu" i pokazuje zobowiązania wszystkich. Dlatego **każdy wiersz ma kolumnę
 * z elementem** — to jedyna rzecz, która wiersze tu odróżnia.
 *
 * Kolejność każdej listy jest **ta sama co porządek jej kafla na pulpicie** (`model/dashboard`),
 * więc „pierwsze cztery" z kafla to naprawdę pierwsze cztery z listy.
 *
 * ⏳ Zakres to dziś **suma „moje + mojego zespołu"** — tak liczy serwer
 * (`GetElementsOwnedByAsync([userId], myTeams)`) i nie zwraca znacznika, który wiersz
 * jest czyj. Rozdzielenie na osobne listy wymaga zmiany po stronie API.
 */

/** Wspólny nagłówek kolumn: znak stanu, zobowiązanie, element. */
const LEAD = ['', 'lists.column.commitment', 'lists.column.element'] as const;

function useLists(): Dashboard | undefined {
  return useDashboard().data;
}

// ── Zadania na mnie ─────────────────────────────────────────────────────────

export function MyCommitmentsScreen() {
  const dashboard = useLists();
  const rows = dashboard ? hangingList(dashboard) : [];

  return (
    <WorkListLayout
      titleKey="app.nav.myCommitments"
      hintKey="lists.hint.hanging"
      emptyKey="lists.empty.hanging"
      count={rows.length}
      columns={[...LEAD, 'lists.column.waiting', '']}
    >
      {rows.map((commitment) => (
        <tr key={commitment.id}>
          <Mark icon={TILE_ICONS.hanging} />
          <TitleCell commitment={commitment} />
          <ElementLink id={commitment.hostElementId} title={commitment.hostElementTitle} />
          <AgeCell since={waitingSince(commitment)} />
          <Actions commitment={commitment} />
        </tr>
      ))}
    </WorkListLayout>
  );
}

// ── Obudzone surty ──────────────────────────────────────────────────────────

export function WokenSurtsScreen() {
  const dashboard = useLists();
  const rows = dashboard ? firedList(dashboard) : [];

  return (
    <WorkListLayout
      titleKey="app.nav.wokenSurts"
      hintKey="lists.hint.fired"
      emptyKey="lists.empty.fired"
      count={rows.length}
      columns={[...LEAD, 'lists.column.wokenBy', 'lists.column.since', '']}
    >
      {rows.map((commitment) => (
        <tr key={commitment.id}>
          <Mark icon={TILE_ICONS.fired} tone="fired" />
          <TitleCell commitment={commitment} />
          <ElementLink id={commitment.hostElementId} title={commitment.hostElementTitle} />
          <Why text={commitment.surtSourceTitle} />
          <AgeCell since={waitingSince(commitment)} />
          <Actions commitment={commitment} />
        </tr>
      ))}
    </WorkListLayout>
  );
}

// ── Czeka na surt ───────────────────────────────────────────────────────────

export function WaitingSurtsScreen() {
  const { t } = useTranslation();
  const dashboard = useLists();
  const rows = dashboard ? dormantList(dashboard) : [];

  return (
    <WorkListLayout
      titleKey="app.nav.waitingForSurt"
      hintKey="lists.hint.dormant"
      emptyKey="lists.empty.dormant"
      count={rows.length}
      columns={[...LEAD, 'lists.column.condition', 'lists.column.sleeping', '']}
    >
      {rows.map((commitment) => (
        <tr key={commitment.id}>
          <Mark icon={TILE_ICONS.dormant} tone="sleeping" />
          <TitleCell commitment={commitment} />
          <ElementLink id={commitment.hostElementId} title={commitment.hostElementTitle} />
          <Why
            text={
              commitment.surtSourceTitle ??
              (commitment.iterationNumber !== null
                ? t('commitments.why.iteration', { number: commitment.iterationNumber })
                : null)
            }
          />
          <AgeCell since={waitingSince(commitment)} />
          {/*
            Śpiące zobowiązanie da się zamknąć jako nieaktualne, choć nigdy nie zostało
            obudzone — i to jest sedno drugiej osi (spec sek. 3): rzecz przestaje mieć sens,
            zanim ktokolwiek się nią zajmie. Bez tego jedyną drogą byłoby czekanie na surt,
            który nigdy nie przyjdzie.
          */}
          <Actions commitment={commitment} />
        </tr>
      ))}
    </WorkListLayout>
  );
}

// ── Czeka na iterację ─────────────────────────────────────────────────────────

/**
 * Śpiące wyłącznie na odcięcie iteracji elementu (decyzja 92). Bez tej listy zadanie
 * kampanii w trybie „iteracja" nie miałoby miejsca, w którym właściciel widzi, że coś
 * na jego iterację czeka — a droga wyjścia jest jedna i konkretna: „Zacznij iterację"
 * na elemencie (panel zobowiązań).
 */
export function WaitingIterationScreen() {
  const { t } = useTranslation();
  const dashboard = useLists();
  const rows = dashboard ? iterationList(dashboard) : [];

  return (
    <WorkListLayout
      titleKey="app.nav.waitingForIteration"
      hintKey="lists.hint.iteration"
      emptyKey="lists.empty.iteration"
      count={rows.length}
      columns={[...LEAD, 'lists.column.iteration', 'lists.column.sleeping', '']}
    >
      {rows.map((commitment) => (
        <tr key={commitment.id}>
          <Mark icon={TILE_ICONS.iteration} tone="sleeping" />
          <TitleCell commitment={commitment} />
          <ElementLink id={commitment.hostElementId} title={commitment.hostElementTitle} />
          <Why
            text={
              commitment.iterationNumber !== null
                ? t('commitments.why.iteration', { number: commitment.iterationNumber })
                : null
            }
          />
          <AgeCell since={waitingSince(commitment)} />
          {/* Śpiące na iterację też da się zamknąć jako nieaktualne — druga oś (spec sek. 3). */}
          <Actions commitment={commitment} />
        </tr>
      ))}
    </WorkListLayout>
  );
}

// ── Martwe surty ────────────────────────────────────────────────────────────

export function DeadSurtsScreen() {
  const dashboard = useLists();
  const rows = dashboard ? draugrList(dashboard) : [];

  return (
    <WorkListLayout
      titleKey="app.nav.deadSurts"
      hintKey="lists.hint.draugr"
      emptyKey="lists.empty.draugr"
      count={rows.length}
      columns={[...LEAD, 'lists.column.killedBy', 'lists.column.orphaned', '']}
    >
      {rows.map((commitment) => (
        /*
          Jedyna lista, na której KAŻDY wiersz wymaga decyzji człowieka: system nie zamknie
          draugra sam (decyzja 21), a osierocenie cofa się wyłącznie przez ponowne otwarcie
          źródła. Stąd tło ostrzegawcze na całym wierszu, a nie tylko znak w pierwszej kolumnie.
        */
        <tr key={commitment.id} className={styles['rowDanger']}>
          <Mark icon={TILE_ICONS.draugr} tone="draugr" />
          <TitleCell commitment={commitment} />
          <ElementLink id={commitment.hostElementId} title={commitment.hostElementTitle} />
          <Why text={commitment.surtSourceTitle} />
          <AgeCell since={waitingSince(commitment)} />
          <Actions commitment={commitment} />
        </tr>
      ))}
    </WorkListLayout>
  );
}

// ── Do wiadomości ───────────────────────────────────────────────────────────

export function InboxScreen() {
  const { t } = useTranslation();
  const dashboard = useLists();
  const rows = dashboard ? noticeList(dashboard) : [];

  return (
    <WorkListLayout
      titleKey="app.nav.inbox"
      hintKey="lists.hint.notices"
      emptyKey="lists.empty.notices"
      count={rows.length}
      columns={['', 'lists.column.whatHappened', 'lists.column.element', 'lists.column.when', '']}
    >
      {rows.map((notice) => (
        <tr key={notice.id}>
          <Mark
            icon={notice.kind === 'deadSurt' ? TILE_ICONS.draugr : TILE_ICONS.dormant}
            tone={notice.kind === 'deadSurt' ? 'draugr' : 'sleeping'}
          />

          <td className={styles['titleCell']} title={notice.commitment.title}>
            {notice.commitment.title}
            <div className={styles['why']}>
              {t(`dashboard.tile.meta.notice.${notice.kind}`, { source: notice.causeTitle ?? '' })}
            </div>
          </td>

          <ElementLink
            id={notice.commitment.hostElementId}
            title={notice.commitment.hostElementTitle}
          />

          <AgeCell since={notice.createdAtUtc} />

          <td className={styles['actions']}>
            <AcknowledgeButton notice={notice} />
          </td>
        </tr>
      ))}
    </WorkListLayout>
  );
}

// ── Co uruchomiłem ──────────────────────────────────────────────────────────

export function LaunchedScreen() {
  const { t } = useTranslation();
  const dashboard = useLists();
  const rows = dashboard ? launchedList(dashboard) : [];

  return (
    <WorkListLayout
      titleKey="app.nav.launched"
      hintKey="lists.hint.launched"
      emptyKey="lists.empty.launched"
      count={rows.length}
      columns={[...LEAD, 'lists.column.myCause', 'lists.column.state']}
    >
      {rows.map((row: LaunchedCommitment) => (
        /*
          ⛔ Jedyna lista BEZ akcji — i to nie jest brak, tylko treść. Pokazuje skutki mojej
          roboty na CUDZYCH elementach (jedyna soczewka niezależna od własności), więc
          zamykanie tych zadań nie należy do mnie. Wiersz odpowiada na „czy ktoś to ruszył".
        */
        <tr key={`${row.commitment.id}-${row.launchedAtUtc}`}>
          <Mark icon={TILE_ICONS.launched} />
          <TitleCell commitment={row.commitment} />
          <ElementLink
            id={row.commitment.hostElementId}
            title={row.commitment.hostElementTitle}
          />
          <Why text={row.causeTitle} />
          <td className={styles['state']}>
            {t(`lists.state.${row.commitment.done ? 'done' : row.commitment.valid ? 'open' : 'obsolete'}`)}
          </td>
        </tr>
      ))}
    </WorkListLayout>
  );
}

// ── Wspólne komórki ─────────────────────────────────────────────────────────

/**
 * Znak listy w pierwszej kolumnie.
 *
 * ⭐ Ikona przychodzi z `TILE_ICONS`, czyli z tego samego miejsca co kafel pulpitu i pozycja
 * menu — te trzy widoki opisują TĘ SAMĄ listę i nie mogą wyglądać na trzy różne rzeczy.
 * ⚠ Do 2026-08-19 stały tu znaki czcionki (`✓ ⚡ 💤 📦 ⚠ →`) mimo spec 12B.7; rozjazd z menu
 * był dokładnie tym, przed którym ostrzega komentarz przy `TILE_ICONS`.
 */
function Mark({ icon: Icon, tone }: { icon: LucideIcon; tone?: 'draugr' | 'fired' | 'sleeping' }) {
  const toneClass = tone ? styles[`mark${tone[0]!.toUpperCase()}${tone.slice(1)}`] : undefined;

  return (
    <td className={`${styles['mark']} ${toneClass ?? ''}`} aria-hidden="true">
      <Icon size={14} />
    </td>
  );
}

function TitleCell({ commitment }: { commitment: CommitmentSummary }) {
  return (
    <td className={styles['titleCell']} title={commitment.title}>
      {commitment.title}
    </td>
  );
}

/** „Dlaczego to tu stoi" — myślnik zamiast pustki, żeby kolumna nie wyglądała na urwaną. */
function Why({ text }: { text: string | null }) {
  return <td className={styles['why']}>{text ?? '—'}</td>;
}

function Actions({ commitment }: { commitment: CommitmentSummary }) {
  return (
    <td className={styles['actions']}>
      <CloseButtons commitment={commitment} />
    </td>
  );
}

function AcknowledgeButton({ notice }: { notice: DashboardNotice }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const acknowledge = useAcknowledgeNotice();

  return (
    <Button
      variant="ghost"
      disabled={acknowledge.isPending}
      onClick={() =>
        acknowledge.mutate(notice.id, {
          onSuccess: () => show({ message: t('dashboard.acknowledged'), tone: 'success' }),
          onError: (error) => show({ message: describeError(error), tone: 'danger' }),
        })
      }
    >
      {t('dashboard.acknowledgeFull')}
    </Button>
  );
}
